import { useState } from "react";
import { Link } from "react-router-dom";
import { Barcode, Camera, MapPin } from "@phosphor-icons/react";
import { PortalLightbox } from "@/components/PortalLightbox";
import { API } from "@/lib/api";

const src = (path, w) =>
  `${API}/photos/${path}?auth=${localStorage.getItem("sw_token")}${w ? `&w=${w}` : ""}`;

const STATUS = {
  Reserved: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  "In Stock": "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Consumed: "border-zinc-600 bg-zinc-800 text-zinc-300",
};

// The exact slab pieces reserved to a job: the piece's own photo, serial and where it sits.
export const ReservedPieces = ({ pieces, compact = false, testPrefix = "reserved-piece" }) => {
  const [light, setLight] = useState(null);
  if (!pieces?.length) return null;
  return (
    <>
      <div className={compact ? "space-y-3" : "grid gap-3 sm:grid-cols-2"}>
        {pieces.map((p) => {
          const own = p.photo_source === "piece";
          const title = p.serial_number ? `SN ${p.serial_number}` : p.name || p.slab_color;
          return (
            <div
              key={p.piece_id}
              data-testid={`${testPrefix}-${p.piece_id}`}
              className="flex gap-3 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
            >
              <button
                type="button"
                data-testid={`${testPrefix}-photo-${p.piece_id}`}
                disabled={!p.photos?.length}
                onClick={() => setLight({ piece: p, index: 0 })}
                title={p.photos?.length ? "Zoom the slab photo" : ""}
                className={`relative shrink-0 bg-zinc-900 ${compact ? "h-24 w-28" : "h-28 w-36"} ${
                  p.photos?.length ? "cursor-zoom-in" : "cursor-default"
                }`}
              >
                {p.thumb_path ? (
                  <img
                    src={src(p.thumb_path, 400)}
                    alt={title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-zinc-600">
                    <Camera size={16} weight="bold" /> No photo
                  </span>
                )}
                {p.thumb_path && (
                  <span
                    data-testid={`${testPrefix}-photo-source-${p.piece_id}`}
                    className={`absolute bottom-1 left-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
                      own ? "bg-emerald-500 text-black" : "bg-black/70 text-zinc-200"
                    }`}
                    title={own ? "Photo of this exact piece" : "Slab photo — this piece has not been photographed yet"}
                  >
                    {own ? "This piece" : "Slab photo"}
                  </span>
                )}
                {(p.photos?.length || 0) > 1 && (
                  <span className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-bold text-white">
                    {p.photos.length}
                  </span>
                )}
              </button>
              <div className="min-w-0 flex-1 py-2 pr-3">
                <div className="flex items-center gap-1.5">
                  <Barcode size={14} weight="bold" className="shrink-0 text-indigo-300" />
                  <span
                    data-testid={`${testPrefix}-serial-${p.piece_id}`}
                    className="font-mono-data truncate text-sm font-bold text-indigo-200"
                  >
                    {title}
                  </span>
                </div>
                <div className="mt-0.5 truncate text-sm text-zinc-200">
                  {p.slab_color}
                  {p.slab_code ? (
                    <span className="font-mono-data ml-1.5 text-xs text-zinc-500">{p.slab_code}</span>
                  ) : null}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
                  {p.lot_number && (
                    <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                      Lot {p.lot_number}
                    </span>
                  )}
                  {p.bundle_number && (
                    <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                      Bundle {p.bundle_number}
                    </span>
                  )}
                  {p.size && (
                    <span className="font-mono-data rounded-full border border-zinc-800 px-2 py-0.5">
                      {p.size}
                    </span>
                  )}
                  <span className="font-mono-data rounded-full border border-yellow-500/25 bg-yellow-500/10 px-2 py-0.5 text-yellow-400">
                    {p.sqft} ft²
                  </span>
                  <span
                    className={`rounded-full border px-2 py-0.5 ${STATUS[p.status] || "border-zinc-800"}`}
                  >
                    {p.status}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400">
                  {p.location && (
                    <span
                      data-testid={`${testPrefix}-location-${p.piece_id}`}
                      className="inline-flex items-center gap-1"
                    >
                      <MapPin size={12} weight="bold" className="text-zinc-500" /> Rack {p.location}
                    </span>
                  )}
                  {[p.material, p.thickness, p.finish].filter(Boolean).join(" · ")}
                  <Link
                    to={`/inventory/slabs/${p.slab_id}`}
                    data-testid={`${testPrefix}-open-${p.piece_id}`}
                    className="text-indigo-300 hover:underline"
                  >
                    Open slab →
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {light && (
        <PortalLightbox
          prefix={`${testPrefix}-lightbox`}
          shots={light.piece.photos.map((ph) => ({
            path: ph.path,
            thumb: ph.thumb,
            title: `${light.piece.slab_color} · ${light.piece.serial_number ? `SN ${light.piece.serial_number}` : light.piece.name || "Piece"}`,
            sub: [
              light.piece.photo_source === "slab" ? "Slab photo" : "This piece",
              light.piece.lot_number && `Lot ${light.piece.lot_number}`,
              light.piece.size,
              light.piece.location && `Rack ${light.piece.location}`,
            ]
              .filter(Boolean)
              .join(" · "),
          }))}
          index={light.index}
          srcOf={(path) => src(path)}
          onClose={() => setLight(null)}
        />
      )}
    </>
  );
};
